// What Recordings and dates and Webinars read and write (docs/data-model.md 10.4, 10.4.2), as the
// staff member, with their own session, through row level security: every write here is a direct
// write the policies and guard triggers decide (recordings, recording_content and homework_items:
// owner, admin and support; sessions, cohorts, cohort_links, resource placements and client session
// notes: owner and admin; webinars, added and changed by hand: owner and admin; registers: owner,
// admin, support and a host for their product). The functions used are sign-upload's resource branch (a
// recording's files) and, for a webinar's or a course recording's thumbnail (Adam, 9 Oct; decision 47,
// 12 Oct), sign-upload's cover start and sign-file's webinars or recordings target. Nothing here sends an email or reads Zoom (Adam, 5 Oct, stage 6): a
// recording switched on reaches members through the bell, which the database writes as it goes live.
import { COLUMNS } from './columns.js'
import { COPY } from './copy.js'
import {
  homeworkPlan, chaptersFromText, keyMessagesFromText, parseTags, sessionsForCohort, nextCohort, fitWithin, isSixteenNine,
} from './model.js'
import { ukDayOf } from '../ui/format.js'
import { isPlaceholder } from '../../lib/format.js'
import { moduleFor } from '../products/model.js'

const q = (api, table) => api.from(table).select(COLUMNS[table])
const OA = role => role === 'owner' || role === 'admin'

// ---- Reads ------------------------------------------------------------------------------------------

// Everything Recordings and Cohorts and dates draw. A host reads only their product's cohorts and
// sessions (and never the recordings tab); support reads no 1-2-1 space.
export async function loadContent(api, role) {
  const host = role === 'host'
  const none = Promise.resolve([])
  const [products, cohorts, links, modules, sessions, recordings, content, homework, sections, placements, spaces] = await Promise.all([
    api.run(q(api, 'catalogue').order('sort_order').order('id')),
    api.run(q(api, 'cohorts').order('sort_order').order('id')),
    api.run(q(api, 'cohort_links').order('cohort_id')),
    api.run(q(api, 'modules').order('sort_order').order('id')),
    api.run(q(api, 'sessions').order('starts_at', { nullsFirst: false }).order('id')),
    host ? none : api.run(q(api, 'recordings').order('sort_order').order('id')),
    host ? none : api.run(q(api, 'recording_content').order('recording_id')),
    host ? none : api.run(q(api, 'homework_items').is('removed_at', null).order('sort_order').order('id')),
    OA(role) ? api.run(q(api, 'resource_sections').order('sort_order').order('id')) : none,
    host ? none : api.run(q(api, 'resource_placements').not('recording_id', 'is', null).is('removed_at', null).order('id')),
    OA(role) ? api.run(q(api, 'client_spaces').is('ended_at', null).order('opened_at').order('id')) : none,
  ])
  let people = []
  let clientSessions = []
  let notes = []
  if (spaces.length) {
    const ids = [...new Set(spaces.map(s => s.client_person_id))].sort()
    ;[people, clientSessions] = await Promise.all([
      api.run(q(api, 'people').in('id', ids).order('id')),
      api.run(q(api, 'client_sessions').in('space_id', spaces.map(s => s.id).sort()).order('number').order('id')),
    ])
    if (clientSessions.length) {
      notes = await api.run(q(api, 'client_session_notes').in('session_id', clientSessions.map(s => s.id).sort()).order('session_id'))
    }
  }
  return { products, cohorts, links, modules, sessions, recordings, content, homework, sections, placements, spaces, people, clientSessions, notes }
}

// A held session's register (hub.session_register): everyone expected, and anyone already on it
export const loadRegister = (api, sessionId) => api.session_register({ session: sessionId })

// A held session's register lines as hub.attendance holds them, for a mastermind's caught-up days (owner, admin:
// the Accelerator critique's admin 3, 20 Oct): { <person id>: caught_up_on }
export async function loadCatchUps(api, sessionId) {
  const rows = await api.run(q(api, 'attendance').eq('session_id', sessionId).order('person_id'))
  return Object.fromEntries(rows.filter(r => r.caught_up_on).map(r => [r.person_id, r.caught_up_on]))
}

// How far each held session's register has got, for Cohorts and dates' held rows (the Accelerator critique's admin 7,
// 20 Oct): every register line on them (hub.attendance, as the policies let the caller read it, 1,000 rows a read so
// none is cut off), and for those held in the last 14 days hub.session_register too, for who's on the register.
// Answers { <session id>: { marked, total } }, total null for an older session. Cohorts and dates draws without it
// if it fails (each held row says Held, as it did).
export const RECENT_MS = 14 * 86400000
const PAGE = 1000
export async function loadRegisterCounts(api, held, nowMs) {
  const ids = [...new Set(held.map(s => s.id))].sort()
  if (!ids.length) return {}
  const lines = []
  for (let from = 0; ; from += PAGE) {
    const page = await api.run(q(api, 'attendance').in('session_id', ids).order('session_id').order('person_id').range(from, from + PAGE - 1))
    lines.push(...page)
    if (page.length < PAGE) break
  }
  const out = Object.fromEntries(ids.map(id => [id, { marked: lines.filter(l => l.session_id === id && l.status).length, total: null }]))
  const recent = held.filter(s => s.starts_at && Date.parse(s.starts_at) > nowMs - RECENT_MS)
  const registers = await Promise.all(recent.map(s => loadRegister(api, s.id).then(rows => [s.id, rows || []], () => null)))
  for (const got of registers) {
    if (!got) continue
    const [id, rows] = got
    out[id] = { marked: rows.filter(r => r.status).length, total: rows.length }
  }
  return out
}

// Webinars: the catalogue (for What it sells), every webinar, the counts (hub.webinar_stats) and how many
// have registered on each register page (hub.webinar_register_counts: owner and admin, so support's comes
// back empty and the screen leaves Registrations out)
export async function loadWebinars(api, role = 'owner') {
  const [products, webinars, stats, registerCounts] = await Promise.all([
    api.run(q(api, 'catalogue').order('sort_order').order('id')),
    api.run(q(api, 'webinars').order('starts_at').order('id')),
    api.webinar_stats(),
    OA(role) ? api.webinar_register_counts().catch(() => []) : Promise.resolve([]),
  ])
  return { products, webinars, stats, registerCounts: registerCounts || [] }
}

// Who registered on 1 webinar's register page, newest first (hub.replay_registrations_for: owner and admin)
export const loadRegistrations = (api, webinarId) => api.replay_registrations_for({ webinar: webinarId })

// The property strategy tool's completions, newest first, at most 1,000 (Stage 2, S2.9): read under the table's own
// policy (owner and admin at 2-step login read every row), so no staff read function is added
export const STRATEGY_LIMIT = 1000
export const STRATEGY_LIST_COLUMNS = 'id, person_id, first_name, email, created_at, source, strategy, strategy_name, lead_status, made_account, person:people(is_test)'
export const loadStrategyResults = api => api.run(api.from('strategy_results').select(STRATEGY_LIST_COLUMNS)
  .order('created_at', { ascending: false }).order('id').limit(STRATEGY_LIMIT))

// ---- Recordings ---------------------------------------------------------------------------------------

// The recording an item makes on its first save: a session's (a week, a mastermind group's session), a
// module's with no session (a new on-demand course's) or a masterclass's video (the product-types plan,
// 13 Oct). Its title is never a placeholder: the week's or module's own title, else Vimeo's, else the
// session's, else the product's name. A live course's or an on-demand course's recording takes its
// module's key while it's free. A mastermind's group session is named "<product>, <group>, <d Month
// yyyy>" ("The Property Accelerator, Group 2, 25 October 2026", Adam, 25 Oct) and always gets a key
// ("group-2-25-october-2026", -2 and on if taken): the member side shows Watch only for a recording
// with a key, and admin made the Accelerator's with none until 25 Oct.
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const longDay = day => { const [y, m, d] = day.split('-').map(Number); return `${d} ${MONTHS[m - 1]} ${y}` }
const slug = s => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
function freeKey(base, taken) {
  if (!taken.has(base)) return base
  let n = 2
  while (taken.has(`${base}-${n}`)) n++
  return `${base}-${n}`
}
function newRecording(item, { personId, vimeoTitle, taken }) {
  const { product, session, module, cohort } = item
  const day = session?.starts_at ? ukDayOf(session.starts_at) : null
  const mastermind = product.type === 'mastermind'
  const dated = mastermind && cohort?.name && day ? `${cohort.name}, ${longDay(day)}` : null
  const keyed = ['live_cohort', 'on_demand'].includes(product.type) && module && !taken.has(module.key)
  const moduleTitle = module && !isPlaceholder(module.title) ? module.title : null
  const title = (dated ? `${product.name}, ${dated}` : null) || (mastermind ? null : moduleTitle) || vimeoTitle || session?.title || product.name
  return {
    product_id: product.id,
    module_id: session?.module_id || module?.id || null,
    session_id: session?.id ?? null,
    cohort_id: mastermind ? session?.cohort_id ?? null : null,
    key: keyed ? module.key : mastermind ? freeKey(slug(dated || title) || 'session', taken) : null,
    title,
    recorded_on: day,
    sort_order: module?.sort_order ?? 0,
    updated_by: personId,
  }
}

// Saves the editor's boxes, then switches the recording on if asked. form:
//   { link: { url, id, hash }, seconds, chapters (text), summary, keyMessages (text), homework (text),
//     tags (text), thumbnailPath (decision 47, 12 Oct: the path in the covers bucket, null for none;
//     left out, the recording's thumbnail isn't touched) }
// Answers the recording's id. Each write is the database's to refuse (guard_recording_publish: no
// switching on without the link and the length).
export async function saveRecording(api, { item, form, publish, personId, data, shownHomework, vimeoTitle }) {
  let recordingId = item.recording?.id || null
  if (!recordingId) {
    const taken = new Set(data.recordings.filter(r => r.product_id === item.product.id && r.key).map(r => r.key))
    const row = newRecording(item, { personId, vimeoTitle, taken })
    if (form.thumbnailPath) row.thumbnail_path = form.thumbnailPath
    const rows = await api.run(api.from('recordings').insert(row).select('id'))
    recordingId = rows[0].id
    // a new week's or module's first recording names it, while its title is still the placeholder it was
    // made with ("[Week 1 title]")
    const module = row.module_id ? (data.modules || []).find(m => m.id === row.module_id) : null
    if (module && isPlaceholder(module.title) && !isPlaceholder(row.title)) {
      await api.run(api.from('modules').update({ title: row.title }).eq('id', module.id).select('id'))
    }
  } else if (form.thumbnailPath !== undefined && (form.thumbnailPath || null) !== (item.recording.thumbnail_path || null)) {
    // the thumbnail, only when it changed (the database holds it to its 1 form, recordings_thumbnail_path)
    const rows = await api.run(api.from('recordings').update({ thumbnail_path: form.thumbnailPath || null, updated_by: personId })
      .eq('id', recordingId).select('id'))
    if (!rows.length) throw new api.AdminError('not_there', 0, api.WORDS.notThere)
  }

  const before = item.content || {}
  const content = {
    recording_id: recordingId,
    vimeo_url: form.link.url,
    vimeo_id: form.link.id,
    vimeo_hash: form.link.hash,
    duration_seconds: form.seconds || null,
    chapters: chaptersFromText(form.chapters, before.chapters || []).chapters,
    summary: form.summary.trim() || null,
    key_messages: keyMessagesFromText(form.keyMessages, before.key_messages || []),
    tags: parseTags(form.tags),
  }
  await api.run(api.from('recording_content').upsert(content, { onConflict: 'recording_id' }).select('recording_id'))

  const moduleId = item.recording?.module_id ?? item.session?.module_id ?? item.module?.id ?? null
  if (moduleId) {
    const plan = homeworkPlan(form.homework, shownHomework, { productId: item.product.id, moduleId, recordingId })
    for (const u of plan.updates) {
      await api.run(api.from('homework_items').update(u.patch).eq('id', u.id).select('id'))
    }
    if (plan.inserts.length) await api.run(api.from('homework_items').insert(plan.inserts).select('id'))
    if (plan.removals.length) {
      await api.run(api.from('homework_items').update({ removed_at: new Date().toISOString() }).in('id', plan.removals).select('id'))
    }
  }

  if (publish) {
    const rows = await api.run(api.from('recordings').update({ is_published: true, updated_by: personId }).eq('id', recordingId).select('id, is_published, published_at'))
    if (!rows.length) throw new api.AdminError('not_there', 0, api.WORDS.notThere)
  }
  return recordingId
}

// Switches a live recording off (owner, admin; Adam, 5 Oct, question 5). Members stop seeing it straight
// away; everyone's progress and its published_at are kept, so switching it on again puts it back as it
// was. guard_recording_publish refuses anyone else ("Only an owner or admin can switch a recording off").
export async function switchOffRecording(api, { recordingId, personId }) {
  const rows = await api.run(api.from('recordings').update({ is_published: false, updated_by: personId }).eq('id', recordingId).select('id, is_published'))
  if (!rows.length) throw new api.AdminError('not_there', 0, api.WORDS.notThere)
  return rows[0]
}

// A worksheet or pack on a recording (owner, admin): sign-upload's resource branch puts the file in
// the resources bucket under the product's folder (Adam's answer 7) and makes the item, then a
// placement puts it on this recording, in the product's first section, opening with the recording's
// session (or straight away where there's none).
export async function attachFile(api, { file, product, recording, section, personId }) {
  const contentType = file.type || 'application/octet-stream'
  const start = await api.sign_upload({
    action: 'start', kind: 'resource', product_id: product.id, file_name: file.name, content_type: contentType, size_bytes: file.size,
  })
  const { error } = await api.db.storage.from(start.bucket || 'resources').uploadToSignedUrl(start.path, start.token, file, { contentType })
  if (error) throw new api.AdminError('not_uploaded', 0, api.WORDS.fallback)
  const words = file.name.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' ').trim() || file.name
  const title = words[0].toUpperCase() + words.slice(1)
  const done = await api.sign_upload({ action: 'finish', receipt: start.receipt, title })
  const placement = {
    item_id: done.item_id || start.item_id,
    product_id: product.id,
    section_id: section.id,
    module_id: recording.module_id || null,
    recording_id: recording.id,
    release_kind: recording.session_id ? 'after_session' : 'now',
    release_session_id: recording.session_id || null,
    release_module_id: recording.session_id ? (recording.module_id || null) : null,
    created_by: personId,
  }
  await api.run(api.from('resource_placements').insert(placement).select('id'))
  return title
}

// ---- The 1-2-1 (owner, admin) --------------------------------------------------------------------------

// "Save and show Laura": the link, its length and the notes, shown to the client from the first save
export async function saveClientNotes(api, { session, note, url, seconds, notes }) {
  const row = { session_id: session.id, recording_url: url, duration_seconds: seconds || null, notes: notes.trim() || null }
  if (!note?.shown_at) row.shown_at = new Date().toISOString()
  return (await api.run(api.from('client_session_notes').upsert(row, { onConflict: 'session_id' }).select(COLUMNS.client_session_notes)))[0]
}

// ---- Cohorts and dates (owner, admin) ------------------------------------------------------------------

// A session's date, time, length and Zoom link. Members see the new date straight away; nothing is
// emailed (Adam, 5 Oct, stage 6).
export async function saveSession(api, { session, startsAt, minutes, zoom, personId }) {
  const patch = { starts_at: startsAt, duration_minutes: minutes, zoom_join_url: zoom || null, updated_by: personId }
  const rows = await api.run(api.from('sessions').update(patch).eq('id', session.id).select(COLUMNS.sessions))
  if (!rows.length) throw new api.AdminError('not_there', 0, api.WORDS.notThere)
  return rows[0]
}

// The next intake of a live course, with a session for each of its weeks, none dated yet
export async function addCohort(api, { product, data, personId }) {
  const mine = data.cohorts.filter(c => c.product_id === product.id)
  const latest = mine.slice().sort((a, b) => (b.number ?? 0) - (a.number ?? 0))[0]
  const next = nextCohort(product, data.cohorts)
  const weeks = data.modules.filter(m => m.product_id === product.id && m.number != null).length
  const [cohort] = await api.run(api.from('cohorts').insert({
    product_id: product.id, name: next.name, kind: 'intake', number: next.number,
    schedule_label: latest?.schedule_label || null, weeks_total: latest?.weeks_total || weeks || null,
    sort_order: Math.max(0, ...mine.map(c => c.sort_order ?? 0)) + 1, created_by: personId,
  }).select('id, name'))
  const rows = sessionsForCohort(product, cohort.id, data.modules).map(r => ({ ...r, updated_by: personId }))
  if (rows.length) {
    try {
      await api.run(api.from('sessions').insert(rows).select('id'))
    } catch (e) {
      // no half-made intake: take the cohort away again, then say why
      await api.run(api.from('cohorts').delete().eq('id', cohort.id).select('id')).catch(() => {})
      throw e
    }
  }
  return cohort
}

// A new on-demand course's next module (made in admin: the product-types plan, 13 Oct): Module <n>, its
// title a placeholder until its first recording names it
export async function addModule(api, { product, data }) {
  const mine = (data.modules || []).filter(m => m.product_id === product.id)
  const n = Math.max(0, ...mine.map(m => m.number ?? 0)) + 1
  const row = { ...moduleFor(n), sort_order: Math.max(0, ...mine.map(m => m.sort_order ?? 0)) + 1, product_id: product.id }
  const [made] = await api.run(api.from('modules').insert(row).select('id, label'))
  return made
}

// A mastermind group's next session, with no date yet: the last session's length and Zoom link, so only the date
// is left to pick (its time is offered from the group's last dated session: DatesTab's SessionBox)
export async function addGroupSession(api, { product, cohort, data, personId }) {
  const last = data.sessions.filter(s => s.cohort_id === cohort.id).sort((a, b) => (b.number ?? 0) - (a.number ?? 0))[0]
  const [row] = await api.run(api.from('sessions').insert({
    product_id: product.id, cohort_id: cohort.id, number: last?.number != null ? last.number + 1 : null,
    title: last?.title || `${cohort.name} session`, duration_minutes: last?.duration_minutes || product.default_session_minutes || 90,
    ...(last?.zoom_join_url ? { zoom_join_url: last.zoom_join_url } : {}),
    status: 'scheduled', updated_by: personId,
  }).select('id'))
  return row
}

// The Zoom link a mastermind group's later sessions without 1 can take: the latest session of the group with a link
// (typed by Adam, or come with Zoom's schedule), and those later sessions. The hub never reads Zoom.
export function zoomFillFor(sessions, cohortId, nowMs) {
  const mine = sessions.filter(s => s.cohort_id === cohortId && s.status !== 'cancelled')
  const source = mine.filter(s => s.zoom_join_url && s.starts_at).sort((a, b) => Date.parse(b.starts_at) - Date.parse(a.starts_at))[0]
  const later = mine.filter(s => s.starts_at && Date.parse(s.starts_at) > nowMs && !s.zoom_join_url)
  return source && later.length ? { url: source.zoom_join_url, later } : null
}
// ...given it in 1 write: the group's sessions after now with no link, none cancelled, as zoomFillFor counts them
// (owner, admin, under row level security). Answers how many took it.
export async function fillZoomLinks(api, { cohortId, url, personId, nowMs }) {
  const rows = await api.run(api.from('sessions').update({ zoom_join_url: url, updated_by: personId })
    .eq('cohort_id', cohortId).gt('starts_at', new Date(nowMs).toISOString()).is('zoom_join_url', null).neq('status', 'cancelled').select('id'))
  return rows.length
}

// A group's WhatsApp invite link (cohort_links, upserted)
export async function saveWhatsapp(api, { cohortId, url, personId }) {
  return (await api.run(api.from('cohort_links').upsert({ cohort_id: cohortId, whatsapp_invite_url: url || null, updated_by: personId }, { onConflict: 'cohort_id' })
    .select(COLUMNS.cohort_links)))[0]
}

// An intake's deal surgery switched on or off (Profit On Your Terms; owner and admin, data-model 10.6.2)
export async function saveDealSurgery(api, { cohortId, on }) {
  const rows = await api.run(api.from('cohorts').update({ deal_surgery: Boolean(on) }).eq('id', cohortId).select(COLUMNS.cohorts))
  if (!rows?.length) throw new api.AdminError('not_there', 0, api.WORDS.notThere)
  return rows[0]
}

// ---- Webinars (owner, admin) ----------------------------------------------------------------------------

// A webinar's thumbnail (Adam, 9 Oct). The picked image is read and redrawn in the browser at up to 1280
// by 720, as WebP (JPEG where the browser can't make WebP), so a big PNG doesn't cost members megabytes.
// Answers what's uploaded and the preview, as a data: address (the site's Content-Security-Policy lets
// images come from data:, never blob:), with the picked image's own size, for the 16:9 warning.
const blobOf = (canvas, type) => new Promise(resolve => canvas.toBlob(b => resolve(b), type, 0.86))
const dataUrlOf = blob => new Promise((resolve, reject) => {
  const reader = new FileReader()
  reader.onload = () => resolve(String(reader.result))
  reader.onerror = () => reject(reader.error)
  reader.readAsDataURL(blob)
})

export async function shrinkImage(file) {
  const picture = await createImageBitmap(file)
  const source = { width: picture.width, height: picture.height }
  const size = fitWithin(source.width, source.height)
  const draw = paper => {
    const canvas = document.createElement('canvas')
    canvas.width = size.width
    canvas.height = size.height
    const ctx = canvas.getContext('2d')
    // a JPEG has no see-through parts: those go white, as the page behind the card is
    if (paper) { ctx.fillStyle = paper; ctx.fillRect(0, 0, size.width, size.height) }
    ctx.drawImage(picture, 0, 0, size.width, size.height)
    return canvas
  }
  let blob = await blobOf(draw(null), 'image/webp')
  if (!blob || blob.type !== 'image/webp') blob = await blobOf(draw('#FFFFFF'), 'image/jpeg')
  picture.close?.()
  if (!blob) throw new Error('not drawn')
  const webp = blob.type === 'image/webp'
  return {
    blob, type: blob.type, name: webp ? 'thumbnail.webp' : 'thumbnail.jpg', size,
    source, sixteenNine: isSixteenNine(source.width, source.height), preview: await dataUrlOf(blob),
  }
}

// The redrawn thumbnail into the covers bucket, in the webinar's (or the recording's) own product's
// folder: sign-upload's cover start gives a 1-off upload link, and the image goes straight there.
// finish isn't called (it would make the image the product's cover): the path answered is saved on the
// webinar or the recording with Save.
export async function uploadThumbnail(api, { productId, image }) {
  const start = await api.sign_upload({
    action: 'start', kind: 'cover', product_id: productId, file_name: image.name, content_type: image.type, size_bytes: image.blob.size,
  })
  const { error } = await api.db.storage.from(start.bucket || 'covers').uploadToSignedUrl(start.path, start.token, image.blob, { contentType: image.type })
  if (error || !start.path) throw new api.AdminError('not_uploaded', 0, COPY.thumbnailNotUploaded)
  return start.path
}

const UNREACHED = Symbol('unreached')
// no answer at all (status 0), or the function or its storage failing (a 5xx)
const unreachable = e => !e?.status || e.status >= 500

// sign-file's answer for 1 call (only a 200 is an answer about the thumbnails), or UNREACHED: not
// answered twice, or refused (a 4xx: the login ran out, no person), which is about the caller, not a
// thumbnail, and isn't asked again. Since 10 Oct, viewing as isn't a refusal for this target: it signs
// what the member being viewed can see. AdminGate shuts admin while viewing as, so only an admin tab
// left open while View as starts in another could ask then.
async function signOnceMore(api, group, target = 'webinars') {
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      return await api.sign_file({ [target]: group })
    } catch (e) {
      if (!unreachable(e)) return UNREACHED
    }
  }
  return UNREACHED
}

// The webinars' thumbnails as staff see them, through links sign-file signs for 2 minutes
// ({"webinars": [ids]}, 20 a call), asked only for webinars with 1: { <webinar id>: url, or false where
// sign-file answered and signed none for it }. A call that never gets an answer, or gets a 5xx, is made
// once more; if that fails too, or sign-file refuses it (a 4xx), its webinars are left out, so the
// screen keeps whatever it had for them rather than saying they won't load. With target 'recordings',
// the same for recordings (decision 47, 12 Oct), through sign-file's recordings target.
export async function signThumbnailPreviews(api, webinars, target = 'webinars') {
  const ids = webinars.filter(w => w.thumbnail_path).map(w => w.id)
  const out = {}
  for (let i = 0; i < ids.length; i += 20) {
    const group = ids.slice(i, i + 20)
    const answer = await signOnceMore(api, group, target)
    if (answer === UNREACHED) continue
    for (const id of group) {
      const url = answer?.thumbnails?.[id]?.url
      out[id] = typeof url === 'string' && url ? url : false
    }
  }
  return out
}

// A webinar's insert or update, its thumbnail_path with it. The database takes only Zoom's own registration link
// (webinars_registration_url_check, Adam, 5 Oct, stage 6's batch): its refusal reads as the form's own
// line, where any other refusal keeps its own words.
// A register page's link another webinar has (the unique index on register_slug) and a register page the
// database's checks refuse read as the form's own lines too.
async function writeWebinar(api, query) {
  const res = await query
  const said = `${res?.error?.message || ''} ${res?.error?.details || ''}`
  if (res?.error?.code === '23514' && /registration_url/.test(said)) {
    throw new api.AdminError('23514', 400, COPY.registrationBad)
  }
  if (res?.error?.code === '23505' && /register_slug/.test(said)) throw new api.AdminError('23505', 409, COPY.rrSlugTaken)
  if (res?.error?.code === '23514' && /register/.test(said)) throw new api.AdminError('23514', 400, COPY.rrRefused)
  return api.run(Promise.resolve(res))
}

// A webinar added by hand (Adam, 5 Oct, stage 6): what's typed, off unless switched on. The product
// isn't sent: the database puts it on the webinar product, Free webinars (assign_webinar_defaults),
// stamps who wrote it, and refuses anyone but an owner or admin at 2-step login, not viewing as.
export async function addWebinar(api, { row, personId }) {
  const rows = await writeWebinar(api, api.from('webinars').insert({ ...row, updated_by: personId }).select(COLUMNS.webinars))
  if (!rows?.length) throw new api.AdminError('not_there', 0, api.WORDS.notThere)
  return rows[0]
}

// A webinar's typed details and the hub's own fields (the columns granted to owner and admin)
export async function saveWebinar(api, { id, patch, personId }) {
  const rows = await writeWebinar(api, api.from('webinars').update({ ...patch, updated_by: personId }).eq('id', id).select(COLUMNS.webinars))
  if (!rows.length) throw new api.AdminError('not_there', 0, api.WORDS.notThere)
  return rows[0]
}
